import { useState } from "react";
import { ReportControllerService } from "openapi";
import authorizedCall from "features/common/utils/authorizedCall";
import { ReconciliationModalProps } from "./ReconciliationModal";
import { useUserGrantsContext } from "features/user/hooks/useUserGrantsContext";
import { checkOperationPermission } from "features/auth/utils/checkOperationPermission";
import { useNavigate } from "react-router-dom";
import { toIsoString } from "features/common/lib/date";
import { AlertColor } from "@mui/material";

export interface useReconciliationModalArgs {
  reportType: {
    type: "RECONCILIATION" | "STATISTICS";
    action: "INTERNAL" | "EXTERNAL";
  };
  onClose: ReconciliationModalProps["onClose"];
}

export const useReconciliationModal = ({
  reportType,
  onClose,
}: useReconciliationModalArgs) => {
  const [isExecutingRec, setIsExecutingRec] = useState(false);
  const [executeRecError, setExecuteRecError] = useState("");
  const [noReportCreated, setNoReportCreated] = useState("");

  const { userGrants } = useUserGrantsContext();
  const navigate = useNavigate();

  const pageUrl = `${
    reportType.action === "INTERNAL" ? "internal" : "external"
  }-reconciliation`;

  const handleClose = () => {
    onClose();
    setExecuteRecError("");
  };

  const handleStartRec = () => {
    setExecuteRecError("");
    setIsExecutingRec(true);

    // TODO: check why WbhReportRequest actionType enum in BE does not have "RECONCILIATION" | "STATISTICS"
    authorizedCall(
      ReportControllerService.createReport,
      {
        requestBody: {
          actionType: reportType.action,
          type: reportType.type,
          timestamp: toIsoString(new Date()),
        },
      },
      true
    )
      .then((res) => {
        setIsExecutingRec(false);
        const reportId = res?.reportId;

        if (!reportId) {
          setNoReportCreated(
            "Reconciliation done, there are no discrepancies."
          );
        } else {
          navigate(`${pageUrl}/${reportId}`);
        }
      })
      .catch((error) => {
        setIsExecutingRec(false);

        if (error.status === 504) {
          setExecuteRecError(
            "Process is taking more time than expected. Please check export log after few minutes."
          );
        } else {
          setExecuteRecError(error?.body?.message || error?.message);
        }
      });
  };

  const componentTypeMap = {
    INTERNAL: "Internal List with Host-File (intern)",
    EXTERNAL: "Internal List with HZ-File (extern)",
  };

  const reconciliationIsAllowed =
    userGrants &&
    checkOperationPermission({
      operation: "E",
      component: componentTypeMap[reportType.action],
      func: reportType.action === "INTERNAL" ? "RECON_INT" : "RECON_EXT",
      userGrants,
    });

  const reconciliationError = executeRecError
    ? executeRecError
    : noReportCreated
    ? noReportCreated
    : "";

  const error = "error" as AlertColor;
  const info = "info" as AlertColor;

  const severityMessage = executeRecError
    ? error
    : noReportCreated
    ? info
    : undefined;

  return {
    isExecutingRec,
    handleStartRec,
    handleClose,
    reconciliationIsAllowed,
    severityMessage,
    reconciliationError,
  };
};
